import KanbanAPI from '../../../api/kanban';

const PAGE_SIZE = 20;
const LOAD_MORE_SIZE = 50;

const state = {
  boards: [],
  currentBoard: null,
  columns: [],
  cards: {},
  // Pagination metadata per column id: { total, hasMore, isLoadingMore }
  cardMeta: {},
  // Combined view: full board data (incl. columns) keyed by board id
  boardsData: {},
  // Ids of boards selected for the combined view (1 = single board view)
  selectedBoardIds: [],
  // Card list sort + filters applied to every column
  sort: 'newest',
  filters: {},
  // Search mode: null = inactive, array = active results
  searchQuery: '',
  searchResults: null,
  uiFlags: {
    isFetchingBoards: false,
    isFetchingBoard: false,
    isCreatingBoard: false,
    isUpdatingBoard: false,
    isDeletingBoard: false,
    isCreatingColumn: false,
    isUpdatingColumn: false,
    isDeletingColumn: false,
    isMovingCard: false,
    isSearching: false,
  },
};

// Merge columns from several boards: columns matching by name (case-insensitive)
// then by column_type are fused into one virtual column; the rest appear alone.
const buildMergedColumns = (boardsData, boardIds) => {
  const merged = [];
  const byName = {};
  const byType = {};

  boardIds.forEach(boardId => {
    const board = boardsData[boardId];
    if (!board) return;
    (board.columns || []).forEach(column => {
      const source = {
        boardId: board.id,
        inboxId: board.inbox_id,
        columnId: column.id,
      };
      const nameKey = (column.name || '').trim().toLowerCase();
      const typeKey = column.column_type;

      let target = byName[nameKey] || byType[typeKey];
      if (!target) {
        target = {
          key: `merged-${column.column_type}-${nameKey}`,
          name: column.name,
          color: column.color,
          column_type: column.column_type,
          position: column.position,
          wip_limit: column.wip_limit,
          sources: [],
          columnIds: [],
          inboxIds: [],
        };
        byName[nameKey] = target;
        byType[typeKey] = target;
        merged.push(target);
      }
      target.sources.push(source);
      target.columnIds.push(column.id);
      target.inboxIds.push(board.inbox_id);
    });
  });

  return merged.sort((a, b) => a.position - b.position);
};

const getters = {
  getBoards: $state => $state.boards,
  getCurrentBoard: $state => $state.currentBoard,
  getColumns: $state => $state.columns,
  getCardsByColumn: $state => columnId => $state.cards[columnId] || [],
  getCardMeta: $state => columnId => $state.cardMeta[columnId] || {},
  getUIFlags: $state => $state.uiFlags,
  getBoardByInboxId: $state => inboxId =>
    $state.boards.find(board => board.inbox_id === inboxId),
  getSelectedBoardIds: $state => $state.selectedBoardIds,
  getIsCombined: $state => $state.selectedBoardIds.length > 1,
  getSort: $state => $state.sort,
  getFilters: $state => $state.filters,
  getSearchQuery: $state => $state.searchQuery,
  getSearchResults: $state => $state.searchResults,
  getIsSearching: $state => $state.searchResults !== null,
  getMergedColumns: $state =>
    buildMergedColumns($state.boardsData, $state.selectedBoardIds),
};

const actions = {
  async fetchBoards({ commit }) {
    commit('setUIFlag', { isFetchingBoards: true });
    try {
      const response = await KanbanAPI.getBoards();
      commit('setBoards', response.data);
    } catch (error) {
      console.error('Error fetching kanban boards:', error);
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingBoards: false });
    }
  },

  async fetchBoard({ commit }, boardId) {
    commit('setUIFlag', { isFetchingBoard: true });
    try {
      const response = await KanbanAPI.getBoard(boardId);
      commit('setCurrentBoard', response.data);
      commit('setColumns', response.data.columns);
      commit('setBoardData', response.data);
      commit('ingestBoardCards', response.data.columns);
      return response.data;
    } catch (error) {
      console.error('Error fetching kanban board:', error);
      throw error;
    } finally {
      commit('setUIFlag', { isFetchingBoard: false });
    }
  },

  // Set which boards are shown. A single id = single board view; 2+ = combined
  // view (columns merged by name then type). Loads any boards not yet cached.
  async setSelectedBoards({ commit, dispatch }, boardIds) {
    commit('setSelectedBoardIds', boardIds);
    commit('clearSearch');
    await Promise.all(boardIds.map(id => dispatch('fetchBoard', id)));
  },

  // Load the next page of cards for a column when the user scrolls to the end.
  async loadMoreColumnCards({ commit, state: $state }, { columnId }) {
    const meta = $state.cardMeta[columnId];
    if (!meta || !meta.hasMore || meta.isLoadingMore) return;

    commit('setCardMeta', { columnId, meta: { ...meta, isLoadingMore: true } });
    const offset = ($state.cards[columnId] || []).length;
    try {
      const response = await KanbanAPI.getColumnCards(columnId, {
        limit: LOAD_MORE_SIZE,
        offset,
        sort: $state.sort,
        filters: $state.filters,
      });
      commit('appendCards', { columnId, cards: response.data.cards });
      commit('setCardMeta', {
        columnId,
        meta: { total: response.data.total, hasMore: response.data.has_more, isLoadingMore: false },
      });
    } catch (error) {
      console.error('Error loading more kanban cards:', error);
      commit('setCardMeta', { columnId, meta: { ...meta, isLoadingMore: false } });
    }
  },

  // Reload the first page of a single column (after sort/filter change).
  async fetchColumnCards({ commit, state: $state }, { columnId }) {
    try {
      const response = await KanbanAPI.getColumnCards(columnId, {
        limit: PAGE_SIZE,
        offset: 0,
        sort: $state.sort,
        filters: $state.filters,
      });
      commit('setColumnCards', { columnId, cards: response.data.cards });
      commit('setCardMeta', {
        columnId,
        meta: { total: response.data.total, hasMore: response.data.has_more, isLoadingMore: false },
      });
    } catch (error) {
      console.error('Error fetching kanban column cards:', error);
    }
  },

  // Apply a new sort/filter set and reload every visible column's first page.
  async applyQuery({ commit, dispatch, state: $state }, { sort, filters }) {
    if (sort !== undefined) commit('setSort', sort);
    if (filters !== undefined) commit('setFilters', filters);

    const columnIds = Object.keys($state.cards);
    await Promise.all(
      columnIds.map(columnId => dispatch('fetchColumnCards', { columnId }))
    );
  },

  // Search cards across the selected boards (or all accessible boards).
  async searchCards({ commit, state: $state }, query) {
    commit('setSearchQuery', query);
    if (!query || !query.trim()) {
      commit('clearSearch');
      return;
    }

    commit('setUIFlag', { isSearching: true });
    try {
      const boardIds = $state.selectedBoardIds.length
        ? $state.selectedBoardIds
        : $state.boards.map(b => b.id);
      const response = await KanbanAPI.searchCards(boardIds, query.trim());
      commit('setSearchResults', response.data.cards || []);
    } catch (error) {
      console.error('Error searching kanban cards:', error);
      commit('setSearchResults', []);
    } finally {
      commit('setUIFlag', { isSearching: false });
    }
  },

  clearSearch({ commit }) {
    commit('clearSearch');
  },

  async createBoard({ commit }, { inboxId, name }) {
    commit('setUIFlag', { isCreatingBoard: true });
    try {
      const response = await KanbanAPI.createBoard({ inbox_id: inboxId, name });
      commit('addBoard', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating kanban board:', error);
      throw error;
    } finally {
      commit('setUIFlag', { isCreatingBoard: false });
    }
  },

  async updateBoard({ commit }, { boardId, data }) {
    commit('setUIFlag', { isUpdatingBoard: true });
    try {
      const response = await KanbanAPI.updateBoard(boardId, data);
      commit('updateBoard', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating kanban board:', error);
      throw error;
    } finally {
      commit('setUIFlag', { isUpdatingBoard: false });
    }
  },

  async deleteBoard({ commit }, boardId) {
    commit('setUIFlag', { isDeletingBoard: true });
    try {
      await KanbanAPI.deleteBoard(boardId);
      commit('removeBoard', boardId);
    } catch (error) {
      console.error('Error deleting kanban board:', error);
      throw error;
    } finally {
      commit('setUIFlag', { isDeletingBoard: false });
    }
  },

  async createColumn({ commit, state: $state }, { name, color, slotConfigId }) {
    commit('setUIFlag', { isCreatingColumn: true });
    try {
      const response = await KanbanAPI.createColumn($state.currentBoard.id, {
        kanban_column: {
          name,
          color,
          slot_config_id: slotConfigId,
          position: $state.columns.length,
        },
      });
      commit('addColumn', { ...response.data, cards: [] });
      return response.data;
    } catch (error) {
      console.error('Error creating kanban column:', error);
      throw error;
    } finally {
      commit('setUIFlag', { isCreatingColumn: false });
    }
  },

  async updateColumn({ commit, state: $state }, { columnId, data }) {
    commit('setUIFlag', { isUpdatingColumn: true });
    try {
      const response = await KanbanAPI.updateColumn(
        $state.currentBoard.id,
        columnId,
        { kanban_column: data }
      );
      commit('updateColumn', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating kanban column:', error);
      throw error;
    } finally {
      commit('setUIFlag', { isUpdatingColumn: false });
    }
  },

  async deleteColumn({ commit, state: $state }, columnId) {
    commit('setUIFlag', { isDeletingColumn: true });
    try {
      await KanbanAPI.deleteColumn($state.currentBoard.id, columnId);
      commit('removeColumn', columnId);
    } catch (error) {
      console.error('Error deleting kanban column:', error);
      throw error;
    } finally {
      commit('setUIFlag', { isDeletingColumn: false });
    }
  },

  async reorderColumns({ commit }, { boardId, columnIds }) {
    try {
      await KanbanAPI.reorderColumns(boardId, columnIds);
      // Update local state with new order
      commit('reorderColumns', columnIds);
    } catch (error) {
      console.error('Error reordering kanban columns:', error);
      throw error;
    }
  },

  async bulkUpdateAutomations(_, { boardId, columnId, automations }) {
    try {
      const response = await KanbanAPI.bulkUpdateAutomations(boardId, columnId, automations);
      return response.data;
    } catch (error) {
      console.error('Error updating automations:', error);
      throw error;
    }
  },

  async moveCard(
    { commit },
    { cardId, sourceColumnId, targetColumnId, position }
  ) {
    // Optimistic update
    commit('moveCardOptimistic', { cardId, sourceColumnId, targetColumnId, position });

    commit('setUIFlag', { isMovingCard: true });
    try {
      const response = await KanbanAPI.moveCard(cardId, targetColumnId, position);
      const cardData = response.data;
      
      // Update card in store (without automations_requiring_custom_message)
      const { automations_requiring_custom_message, ...cardWithoutAutomations } = cardData;
      commit('updateCard', { columnId: targetColumnId, card: cardWithoutAutomations });
      
      // Return full response including automations info for the component to handle
      return cardData;
    } catch (error) {
      // Rollback on error
      commit('moveCardOptimistic', {
        cardId,
        sourceColumnId: targetColumnId,
        targetColumnId: sourceColumnId,
        position: 0,
      });
      console.error('Error moving kanban card:', error);
      throw error;
    } finally {
      commit('setUIFlag', { isMovingCard: false });
    }
  },

  // WebSocket event handlers
  handleCardCreated({ commit }, { card }) {
    commit('addCard', { columnId: card.kanban_column_id, card });
  },

  handleCardUpdated({ commit }, { card }) {
    commit('updateCard', { columnId: card.kanban_column_id, card });
  },

  handleCardMoved({ commit, state: $state }, { card, from_column_id, to_column_id }) {
    // Check if card already exists in target column (avoid duplicates from optimistic updates)
    const targetCards = $state.cards[to_column_id] || [];
    const alreadyExists = targetCards.some(c => c.id === card.id);
    
    if (alreadyExists) {
      // Card already moved via optimistic update, just update the card data
      commit('updateCard', { columnId: to_column_id, card });
    } else {
      // Card not yet in target, perform the move
      commit('removeCardFromColumn', { columnId: from_column_id, cardId: card.id });
      commit('addCard', { columnId: to_column_id, card });
    }
  },

  // Direct WebSocket handlers - no API call needed
  // These receive complete card data from the server
  handleCardCreatedFromCable({ commit, state: $state }, { board_id: boardId, card }) {
    // Only process if we're viewing this board (single or combined view)
    const viewingBoard = $state.selectedBoardIds.length
      ? $state.selectedBoardIds.includes(boardId)
      : $state.currentBoard?.id === boardId;
    if (!viewingBoard) return;

    // Check if card already exists in ANY column to avoid duplicates
    // Check by both card.id AND conversation_id to handle race conditions
    for (const columnCards of Object.values($state.cards)) {
      if (columnCards.some(c => c.id === card.id || c.conversation_id === card.conversation_id)) {
        return; // Card already exists, skip
      }
    }

    // Only add if the column exists in our current view
    if ($state.cards[card.kanban_column_id] !== undefined) {
      commit('addCard', { columnId: card.kanban_column_id, card });
    }
  },

  handleCardUpdatedFromCable({ commit, state: $state }, { card_id: cardId, card }) {
    // Find the card's current column in our state
    let currentColumnId = null;
    let existingCardIndex = -1;

    for (const [columnId, columnCards] of Object.entries($state.cards)) {
      const idx = columnCards.findIndex(c => c.id === cardId);
      if (idx !== -1) {
        currentColumnId = parseInt(columnId, 10);
        existingCardIndex = idx;
        break;
      }
    }

    // Card not found in our current view - might be a different board
    if (currentColumnId === null) {
      return;
    }

    // Check if card moved to a different column
    if (card.kanban_column_id !== currentColumnId) {
      // Remove from old column and add to new column
      commit('removeCardFromColumn', { columnId: currentColumnId, cardId: card.id });
      if ($state.cards[card.kanban_column_id] !== undefined) {
        commit('addCard', { columnId: card.kanban_column_id, card });
      }
    } else {
      // Update in place - force complete replacement for reactivity
      commit('replaceCard', { columnId: currentColumnId, index: existingCardIndex, card });
    }
  },

  // Refresh a card by conversation ID (for real-time updates)
  async refreshCardByConversation({ commit, state: $state }, conversationId) {
    try {
      const response = await KanbanAPI.getCardByConversation(conversationId);
      const card = response.data;

      // Find which column the card is currently in
      let currentColumnId = null;
      let existingCardIndex = -1;
      for (const [columnId, columnCards] of Object.entries($state.cards)) {
        const idx = columnCards.findIndex(c => c.conversation_id === conversationId);
        if (idx !== -1) {
          currentColumnId = parseInt(columnId, 10);
          existingCardIndex = idx;
          break;
        }
      }

      if (currentColumnId !== null && existingCardIndex !== -1) {
        // Check if card moved to a different column
        if (card.kanban_column_id !== currentColumnId) {
          commit('removeCardFromColumn', {
            columnId: currentColumnId,
            cardId: card.id,
          });
          commit('addCard', { columnId: card.kanban_column_id, card });
        } else {
          // Update the card in place - force complete replacement
          commit('replaceCard', { columnId: currentColumnId, index: existingCardIndex, card });
        }
      }

      return card;
    } catch (error) {
      // Card doesn't exist for this conversation, ignore
      if (error.response?.status !== 404) {
        console.error('Error refreshing kanban card:', error);
      }
      return null;
    }
  },

  // Delete a card (used when conversation was deleted)
  async deleteCard({ commit }, { cardId, conversationId }) {
    try {
      await KanbanAPI.deleteCard(cardId);
      commit('removeCardByConversation', conversationId);
    } catch (error) {
      // Even if API fails (card might already be deleted), remove from local state
      commit('removeCardByConversation', conversationId);
      if (error.response?.status !== 404) {
        console.error('Error deleting kanban card:', error);
      }
    }
  },

  // Fetch and add a new card when a conversation is created
  async fetchNewCardByConversation({ commit, state: $state }, { conversationId, inboxId }) {
    // Check if the current board is for this inbox
    if (!$state.currentBoard || $state.currentBoard.inbox_id !== inboxId) {
      return null;
    }

    // Helper function to check if card already exists in any column
    const cardExistsInState = () => {
      for (const cards of Object.values($state.cards)) {
        if (cards.some(c => c.conversation_id === conversationId)) {
          return true;
        }
      }
      return false;
    };

    // Check if card already exists (initial check)
    if (cardExistsInState()) {
      return null; // Card already exists
    }

    // Retry logic with exponential backoff (card creation happens in background job)
    const maxRetries = 5;
    const delays = [300, 600, 1000, 1500, 2000]; // ms

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Re-check if card was added by websocket during our retry loop
        if (cardExistsInState()) {
          return null; // Card was added by another event (e.g., websocket), skip
        }

        const response = await KanbanAPI.getCardByConversation(conversationId);
        const card = response.data;

        // Final check before adding - card might have been added during API call
        if (cardExistsInState()) {
          return card; // Return the data but don't add duplicate
        }

        // Add the card to the appropriate column
        if ($state.cards[card.kanban_column_id] !== undefined) {
          commit('addCard', { columnId: card.kanban_column_id, card });
        }

        return card;
      } catch (error) {
        // Card doesn't exist yet, retry after delay
        if (error.response?.status === 404 && attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, delays[attempt]));
        } else if (error.response?.status !== 404) {
          console.error('Error fetching new kanban card:', error);
          return null;
        }
      }
    }

    return null;
  },

  // Resend the conversation back to the inbox bot: moves the card to the
  // board's creation column, sets it pending and re-dispatches the last
  // customer message to the bot. Returns the API payload, which includes a
  // `requires_template_reactivation` flag for out-of-window WhatsApp Cloud.
  async resendToBot(_, { cardId }) {
    const response = await KanbanAPI.resendToBot(cardId);
    return response.data;
  },

  // Clear bot error flag for a card (manual resolution by agent)
  async clearBotError({ commit, state: $state }, { cardId }) {
    try {
      const response = await KanbanAPI.clearBotError(cardId);
      const card = response.data;
      commit('updateCard', { columnId: card.kanban_column_id, card });
      return card;
    } catch (error) {
      console.error('Error clearing bot error for card:', error);
      throw error;
    }
  },

  // Board Automations
  async fetchBoardAutomations({ commit }, boardId) {
    try {
      const response = await KanbanAPI.getBoardAutomations(boardId);
      return response.data;
    } catch (error) {
      console.error('Error fetching board automations:', error);
      throw error;
    }
  },

  async createBoardAutomation({ commit }, { boardId, data }) {
    try {
      const response = await KanbanAPI.createBoardAutomation(boardId, data);
      return response.data;
    } catch (error) {
      console.error('Error creating board automation:', error);
      throw error;
    }
  },

  async updateBoardAutomation({ commit }, { boardId, automationId, data }) {
    try {
      const response = await KanbanAPI.updateBoardAutomation(boardId, automationId, data);
      return response.data;
    } catch (error) {
      console.error('Error updating board automation:', error);
      throw error;
    }
  },

  async deleteBoardAutomation({ commit }, { boardId, automationId }) {
    try {
      await KanbanAPI.deleteBoardAutomation(boardId, automationId);
    } catch (error) {
      console.error('Error deleting board automation:', error);
      throw error;
    }
  },

  async reorderBoardAutomations({ commit }, { boardId, automationIds }) {
    try {
      await KanbanAPI.reorderBoardAutomations(boardId, automationIds);
    } catch (error) {
      console.error('Error reordering board automations:', error);
      throw error;
    }
  },
};

const mutations = {
  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },

  setBoards($state, boards) {
    $state.boards = boards;
  },

  addBoard($state, board) {
    $state.boards.push(board);
  },

  updateBoard($state, board) {
    const index = $state.boards.findIndex(b => b.id === board.id);
    if (index !== -1) {
      $state.boards.splice(index, 1, board);
    }
    if ($state.currentBoard?.id === board.id) {
      $state.currentBoard = { ...$state.currentBoard, ...board };
    }
  },

  removeBoard($state, boardId) {
    $state.boards = $state.boards.filter(b => b.id !== boardId);
    if ($state.currentBoard?.id === boardId) {
      $state.currentBoard = null;
    }
  },

  setCurrentBoard($state, board) {
    $state.currentBoard = board;
  },

  setColumns($state, columns) {
    $state.columns = columns;
  },

  addColumn($state, column) {
    $state.columns.push(column);
    $state.cards[column.id] = column.cards || [];
  },

  updateColumn($state, column) {
    const index = $state.columns.findIndex(c => c.id === column.id);
    if (index !== -1) {
      $state.columns.splice(index, 1, {
        ...$state.columns[index],
        ...column,
      });
    }
  },

  removeColumn($state, columnId) {
    $state.columns = $state.columns.filter(c => c.id !== columnId);
    delete $state.cards[columnId];
  },

  reorderColumns($state, columnIds) {
    const columnMap = {};
    $state.columns.forEach(col => {
      columnMap[col.id] = col;
    });
    $state.columns = columnIds.map(id => columnMap[id]).filter(Boolean);
  },

  setCards($state, cardsByColumn) {
    $state.cards = cardsByColumn;
  },

  // Merge a board's first-page cards + pagination meta into global state.
  // Keyed by column id, so cards from multiple boards coexist (combined view).
  ingestBoardCards($state, columns) {
    const cards = { ...$state.cards };
    const meta = { ...$state.cardMeta };
    (columns || []).forEach(column => {
      cards[column.id] = column.cards || [];
      meta[column.id] = {
        total: column.cards_count ?? (column.cards || []).length,
        hasMore: column.has_more ?? false,
        isLoadingMore: false,
      };
    });
    $state.cards = cards;
    $state.cardMeta = meta;
  },

  setColumnCards($state, { columnId, cards }) {
    $state.cards = { ...$state.cards, [columnId]: cards };
  },

  appendCards($state, { columnId, cards }) {
    const existing = $state.cards[columnId] || [];
    const seen = new Set(existing.map(c => c.id));
    const merged = [...existing, ...cards.filter(c => !seen.has(c.id))];
    $state.cards = { ...$state.cards, [columnId]: merged };
  },

  setCardMeta($state, { columnId, meta }) {
    $state.cardMeta = {
      ...$state.cardMeta,
      [columnId]: { ...$state.cardMeta[columnId], ...meta },
    };
  },

  setBoardData($state, board) {
    $state.boardsData = {
      ...$state.boardsData,
      [board.id]: {
        id: board.id,
        name: board.name,
        inbox_id: board.inbox_id,
        columns: board.columns || [],
      },
    };
  },

  setSelectedBoardIds($state, boardIds) {
    $state.selectedBoardIds = boardIds;
  },

  setSort($state, sort) {
    $state.sort = sort;
  },

  setFilters($state, filters) {
    $state.filters = filters || {};
  },

  setSearchQuery($state, query) {
    $state.searchQuery = query;
  },

  setSearchResults($state, results) {
    $state.searchResults = results;
  },

  clearSearch($state) {
    $state.searchQuery = '';
    $state.searchResults = null;
  },

  addCard($state, { columnId, card }) {
    const currentCards = $state.cards[columnId] || [];
    
    // Check for duplicates by card.id OR conversation_id to prevent visual duplication
    const isDuplicate = currentCards.some(
      c => c.id === card.id || c.conversation_id === card.conversation_id
    );
    
    if (isDuplicate) {
      // Card already exists, skip adding to prevent duplication
      // eslint-disable-next-line no-console
      console.debug('[Kanban] Skipping duplicate card:', card.id, 'conversation:', card.conversation_id);
      return;
    }
    
    // Add at the beginning (position 0) and trigger reactivity
    $state.cards = {
      ...$state.cards,
      [columnId]: [{ ...card }, ...currentCards],
    };
  },

  updateCard($state, { columnId, card }) {
    const cards = $state.cards[columnId] || [];
    const index = cards.findIndex(c => c.id === card.id);
    if (index !== -1) {
      // Create a new array to trigger Vue reactivity
      const updatedCards = [...cards];
      updatedCards[index] = { ...card };
      $state.cards = { ...$state.cards, [columnId]: updatedCards };
    }
  },

  // Force complete replacement of a card at a specific index
  replaceCard($state, { columnId, index, card }) {
    const cards = $state.cards[columnId];
    if (!cards || index < 0 || index >= cards.length) return;

    // Create completely new array and card object
    const newCards = cards.map((c, i) => {
      if (i === index) {
        // Deep clone the new card data
        return JSON.parse(JSON.stringify(card));
      }
      return c;
    });

    // Replace the entire cards state to ensure reactivity
    $state.cards = { ...$state.cards, [columnId]: newCards };
  },

  removeCardFromColumn($state, { columnId, cardId }) {
    if ($state.cards[columnId]) {
      const filteredCards = $state.cards[columnId].filter(c => c.id !== cardId);
      $state.cards = { ...$state.cards, [columnId]: filteredCards };
    }
  },

  removeCardByConversation($state, conversationId) {
    // Find and remove the card from any column
    const updatedCards = { ...$state.cards };
    for (const [columnId, columnCards] of Object.entries($state.cards)) {
      const idx = columnCards.findIndex(c => c.conversation_id === conversationId);
      if (idx !== -1) {
        updatedCards[columnId] = columnCards.filter(c => c.conversation_id !== conversationId);
        break;
      }
    }
    $state.cards = updatedCards;
  },

  moveCardOptimistic($state, { cardId, sourceColumnId, targetColumnId, position }) {
    // Remove from source
    const sourceCards = [...($state.cards[sourceColumnId] || [])];
    const cardIndex = sourceCards.findIndex(c => c.id === cardId);
    if (cardIndex === -1) return;

    const [card] = sourceCards.splice(cardIndex, 1);

    // Add to target
    const targetCards = [...($state.cards[targetColumnId] || [])];
    targetCards.splice(position, 0, {
      ...card,
      kanban_column_id: targetColumnId,
    });

    // Update state with new object references for reactivity
    $state.cards = {
      ...$state.cards,
      [sourceColumnId]: sourceCards,
      [targetColumnId]: targetCards,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

